import { TApplication, TEntitySection } from '@/interfaces'
import { Collapse, Progress } from 'antd'
import { FormState } from '@/state'
import { Signal, useComputed, useSignal } from '@preact/signals-react'
import { useEffect, useMemo } from 'react'
import RenderSimpleSection from './simple'

function calculatePercentage(
  requiredFields: string[],
  currentValues: TApplication
) {
  const totalFields = requiredFields.length

  if (totalFields === 0) return 100

  const totalFilled = requiredFields.reduce((totalFilled, fieldRequired) => {
    const fieldValue = currentValues[fieldRequired] ?? null

    const isValidValue =
      fieldValue !== null && fieldValue !== undefined && fieldValue !== ''
    if (isValidValue) {
      totalFilled += 1
    }

    return totalFilled
  }, 0)

  return Math.ceil((totalFilled / totalFields) * 100)
}

export default function RenderMultipleSections({
  sections,
}: {
  sections: TEntitySection[]
}) {
  const activeKeys: Signal<string[] | undefined> = useSignal(undefined)
  const panelKeysRequired = useMemo(
    () =>
      sections
        .filter((item) => item.fields?.some((field) => field.required))
        .map((_, index) => `section-${index + 1}`),
    [sections]
  )

  useEffect(() => {
    activeKeys.value = []
  }, [FormState.currentEntity.value?.name])

  useEffect(() => {
    if (FormState.hasRequiredFieldsEmpty.value) {
      activeKeys.value = [...(activeKeys.peek() ?? []), ...panelKeysRequired]
    }
  }, [FormState.hasRequiredFieldsEmpty.value])

  const handleChangeActiveKeys = (newActiveKeys: string | string[]) => {
    activeKeys.value = Array.isArray(newActiveKeys)
      ? newActiveKeys
      : [newActiveKeys]
  }

  const sectionsRequiredfiles = useMemo(() => {
    return sections.map((item) =>
      item.fields?.filter((field) => field.required).map((item) => item.name)
    )
  }, [sections])

  const sectionsProgress = useComputed(() => {
    return sectionsRequiredfiles.map((requiredFields) =>
      calculatePercentage(
        requiredFields as string[],
        FormState.currentFormValues.value
      )
    )
  })

  return (
    <Collapse
      onChange={handleChangeActiveKeys}
      activeKey={activeKeys.value}
      size='small'
      className='collapse-section'
    >
      {sections.map((entitySection, index) => (
        <Collapse.Panel
          key={`section-${index + 1}`}
          header={
            <div className='collapse-panel-header'>
              {`${index + 1} ${entitySection.name?.replace(/^\d+\s*/, '').toUpperCase()}`}
              <Progress
                type='line'
                percent={sectionsProgress.value[index] ?? 0}
                size='small'
              />
            </div>
          }
          className='collapse-panel'
        >
          <RenderSimpleSection
            key={`section-form-${index + 1}`}
            entitySection={entitySection}
          />
        </Collapse.Panel>
      ))}
    </Collapse>
  )
}
